import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FolderIcon from '@mui/icons-material/Folder';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/EditNote';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { pagePaperSx } from '../styles/adminPageStyles';
import { locationLabel } from '../utils/terminology';
import {
  buildFolderPathLabels,
  buildVisibleFolderEntries,
  displayCheckReportSubfolderName,
  isSystemCheckReportFolder,
} from '../utils/checkReportFolders';

export interface FilesExplorerLocation {
  locationId: string;
  locationName: string;
  address?: string;
}

interface LocationDocFolder {
  folder_id: string;
  location_id: string;
  folder_name: string;
  parent_folder_id?: string | null;
}

interface LocationDocument {
  document_id: string;
  location_id: string;
  folder_id: string | null;
  folder_name?: string;
  document_type: string;
  title: string;
  file_url: string;
  file_name: string;
  file_type: string;
  mime_type?: string;
  file_size?: number;
  source: string;
  created_at: string;
}

interface AiAnalysis {
  overall_status: 'COMPLIANT' | 'NEEDS_ATTENTION' | 'NON_COMPLIANT';
  summary: string;
  required_documents?: { document_type: string; label: string; status: string; notes?: string }[];
  action_items?: string[];
}

interface DocReview {
  verdict: string;
  confidence: string;
  summary: string;
  what_is_present?: string[];
  what_is_missing?: string[];
  recommendations?: string[];
}

const UNFILED_FOLDER_ID = '__unfiled__';

const DOC_TYPE_OPTIONS = [
  { value: 'risk_assessment', label: 'Risk Assessment' },
  { value: 'fire_safety', label: 'Fire Safety Certificate' },
  { value: 'insurance', label: 'Insurance Certificate' },
  { value: 'lease', label: 'Lease / Tenancy Agreement' },
  { value: 'permit', label: 'Permit / Licence' },
  { value: 'health_safety', label: 'Health & Safety Policy' },
  { value: 'emergency_plan', label: 'Emergency Plan' },
  { value: 'other', label: 'Other' },
];

const docTypeLabel = (t: string) =>
  DOC_TYPE_OPTIONS.find((o) => o.value === t)?.label || t;

function formatSize(bytes?: number) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDateTime(iso?: string) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function verdictColor(v: string): 'success' | 'warning' | 'error' | 'default' {
  if (v === 'COMPLIANT') return 'success';
  if (v === 'NEEDS_IMPROVEMENT' || v === 'NEEDS_ATTENTION') return 'warning';
  if (v === 'NON_COMPLIANT') return 'error';
  return 'default';
}

function FileTypeIcon({ fileType, fontSize = 'small' as const }: { fileType: string; fontSize?: 'small' | 'medium' }) {
  if (fileType === 'pdf') return <PictureAsPdfIcon fontSize={fontSize} sx={{ color: '#d32f2f' }} />;
  if (fileType === 'image') return <ImageIcon fontSize={fontSize} sx={{ color: '#1976d2' }} />;
  if (fileType === 'text') return <InsertDriveFileIcon fontSize={fontSize} sx={{ color: '#388e3c' }} />;
  return <InsertDriveFileIcon fontSize={fontSize} sx={{ color: '#757575' }} />;
}

const columnSx = {
  flex: '1 1 0',
  minWidth: 180,
  maxWidth: 320,
  display: 'flex',
  flexDirection: 'column',
  borderRight: 1,
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const columnHeaderSx = {
  px: 1.5,
  py: 1,
  borderBottom: 1,
  borderColor: 'divider',
  bgcolor: 'grey.50',
  flexShrink: 0,
};

const selectedRowSx = {
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  '&:hover': { bgcolor: 'primary.dark' },
  '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
  '& .MuiTypography-root': { color: 'inherit' },
};

// ── AI panels ────────────────────────────────────────────────────────────────

function AiLocationAnalysisPanel({ analysis, onDismiss }: { analysis: AiAnalysis; onDismiss: () => void }) {
  const s = analysis.overall_status;
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: s === 'COMPLIANT' ? 'success.50' : s === 'NON_COMPLIANT' ? 'error.50' : 'warning.50', border: 1, borderColor: s === 'COMPLIANT' ? 'success.200' : s === 'NON_COMPLIANT' ? 'error.200' : 'warning.200' }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip label={s.replace(/_/g, ' ')} color={verdictColor(s)} size="small" sx={{ fontWeight: 700 }} />
        <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>{analysis.summary}</Typography>
      </Box>
      {analysis.required_documents && analysis.required_documents.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>Required documents</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {analysis.required_documents.map((rd, i) => (
              <Chip key={i} size="small" label={rd.label}
                color={rd.status === 'present' || rd.status === 'present & valid' ? 'success' : rd.status?.includes('expiring') ? 'warning' : rd.status === 'expired' || rd.status === 'missing' ? 'error' : 'default'}
                variant={rd.status === 'missing' ? 'outlined' : 'filled'} sx={{ fontSize: '0.65rem', height: 20 }} />
            ))}
          </Box>
        </Box>
      )}
      {analysis.action_items && analysis.action_items.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>Actions needed</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {analysis.action_items.map((a, i) => <Typography key={i} component="li" variant="caption">{a}</Typography>)}
          </Box>
        </Box>
      )}
      <Button size="small" onClick={onDismiss}>Dismiss</Button>
    </Box>
  );
}

function AiDocumentReviewPanel({ review, title, onDismiss }: { review: DocReview; title: string; onDismiss: () => void }) {
  const v = review.verdict;
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: v === 'COMPLIANT' ? 'success.50' : v === 'NON_COMPLIANT' ? 'error.50' : v === 'NEEDS_IMPROVEMENT' ? 'warning.50' : 'grey.100', border: 1, borderColor: v === 'COMPLIANT' ? 'success.200' : v === 'NON_COMPLIANT' ? 'error.200' : v === 'NEEDS_IMPROVEMENT' ? 'warning.200' : 'divider' }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{title}</Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" label={v?.replace(/_/g, ' ')} color={verdictColor(v)} sx={{ fontWeight: 700 }} />
        <Chip size="small" label={`Confidence: ${review.confidence}`} variant="outlined" />
      </Box>
      <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>{review.summary}</Typography>
      {review.what_is_present && review.what_is_present.length > 0 && (
        <Box sx={{ mb: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="success.dark" display="block">✓ Present</Typography>
          {review.what_is_present.map((item, i) => <Typography key={i} variant="caption" display="block" sx={{ pl: 1 }}>• {item}</Typography>)}
        </Box>
      )}
      {review.what_is_missing && review.what_is_missing.length > 0 && (
        <Box sx={{ mb: 0.75 }}>
          <Typography variant="caption" fontWeight={700} color="error.dark" display="block">✗ Gaps</Typography>
          {review.what_is_missing.map((item, i) => <Typography key={i} variant="caption" display="block" sx={{ pl: 1 }}>• {item}</Typography>)}
        </Box>
      )}
      {review.recommendations && review.recommendations.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="caption" fontWeight={700} color="warning.dark" display="block">Recommendations</Typography>
          {review.recommendations.map((item, i) => <Typography key={i} variant="caption" display="block" sx={{ pl: 1 }}>→ {item}</Typography>)}
        </Box>
      )}
      <Button size="small" onClick={onDismiss}>Dismiss</Button>
    </Box>
  );
}

interface LocationFilesExplorerProps {
  locations: FilesExplorerLocation[];
  loadingLocations?: boolean;
  onNotify?: (message: string, severity: 'success' | 'error') => void;
  useSiteTerminology?: boolean;
}

const LocationFilesExplorer: React.FC<LocationFilesExplorerProps> = ({ locations, loadingLocations, onNotify, useSiteTerminology }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [folderBrowseParentId, setFolderBrowseParentId] = useState<string | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [folders, setFolders] = useState<LocationDocFolder[]>([]);
  const [documents, setDocuments] = useState<LocationDocument[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const [textEditorOpen, setTextEditorOpen] = useState(false);
  const [textEditorDoc, setTextEditorDoc] = useState<LocationDocument | null>(null);
  const [textEditorContent, setTextEditorContent] = useState('');
  const [textEditorLoading, setTextEditorLoading] = useState(false);
  const [textEditorSaving, setTextEditorSaving] = useState(false);

  const [newTextOpen, setNewTextOpen] = useState(false);
  const [newTextForm, setNewTextForm] = useState({ title: '', document_type: 'other', content: '' });
  const [newTextSaving, setNewTextSaving] = useState(false);

  const [addFolderOpen, setAddFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameType, setRenameType] = useState<'folder' | 'file'>('folder');
  const [renameId, setRenameId] = useState('');
  const [renameValue, setRenameValue] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);

  const [archiving, setArchiving] = useState<string | null>(null);

  // AI inspector state
  const [aiAnalysis, setAiAnalysis] = useState<AiAnalysis | null>(null);
  const [aiAnalysisLoading, setAiAnalysisLoading] = useState(false);
  const [docReviews, setDocReviews] = useState<Record<string, DocReview>>({});
  const [docReviewLoading, setDocReviewLoading] = useState<Record<string, boolean>>({});
  const [inspectorMode, setInspectorMode] = useState<'idle' | 'location' | 'file'>('idle');

  const termLower = locationLabel(!!useSiteTerminology, { lowercase: true });
  const termPluralTitle = locationLabel(!!useSiteTerminology, { plural: true });

  const selectedLocation = useMemo(() => locations.find((l) => l.locationId === selectedLocationId) || null, [locations, selectedLocationId]);

  const loadLocationDocuments = useCallback(async (locationId: string) => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE_URL}/locations/${locationId}/documents`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.success) {
        setFolders(res.data.folders || []);
        setDocuments(res.data.documents || []);
      }
    } catch {
      setFolders([]); setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  const refresh = () => { if (selectedLocationId) loadLocationDocuments(selectedLocationId); };

  const selectLocation = (loc: FilesExplorerLocation) => {
    setSelectedLocationId(loc.locationId);
    setSelectedFolderId(null);
    setFolderBrowseParentId(null);
    setSelectedFileId(null);
    setAiAnalysis(null);
    setDocReviews({});
    setInspectorMode('idle');
    loadLocationDocuments(loc.locationId);
  };

  // ── AI ────────────────────────────────────────────────────────────────────

  const handleRunAiAnalysis = async () => {
    if (!selectedLocationId) return;
    setAiAnalysisLoading(true);
    setAiAnalysis(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/locations/${selectedLocationId}/documents/ai-analysis`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setAiAnalysis(res.data.analysis);
        setInspectorMode('location');
      }
    } catch {
      onNotify?.('AI analysis failed', 'error');
    } finally {
      setAiAnalysisLoading(false);
    }
  };

  const handleRunDocumentReview = async (doc: LocationDocument) => {
    if (!selectedLocationId) return;
    setSelectedFileId(doc.document_id);
    setInspectorMode('file');
    setDocReviewLoading((p) => ({ ...p, [doc.document_id]: true }));
    setDocReviews((p) => { const n = { ...p }; delete n[doc.document_id]; return n; });
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/locations/${selectedLocationId}/documents/${doc.document_id}/review`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data?.success) {
        setDocReviews((p) => ({ ...p, [doc.document_id]: res.data.review }));
      }
    } catch {
      onNotify?.('Document review failed', 'error');
    } finally {
      setDocReviewLoading((p) => ({ ...p, [doc.document_id]: false }));
    }
  };

  // ── Upload ────────────────────────────────────────────────────────────────

  const uploadFile = async (file: File) => {
    if (!selectedLocationId) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.[^.]+$/, ''));
      fd.append('document_type', 'other');
      if (selectedFolderId && selectedFolderId !== UNFILED_FOLDER_ID) {
        fd.append('folder_id', selectedFolderId);
      }
      await axios.post(`${API_BASE_URL}/locations/${selectedLocationId}/documents`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      onNotify?.('File uploaded', 'success');
      refresh();
    } catch {
      onNotify?.('Upload failed', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  // ── Text editor ───────────────────────────────────────────────────────────

  const openTextEditor = async (doc: LocationDocument) => {
    if (!selectedLocationId) return;
    setTextEditorDoc(doc);
    setTextEditorContent('');
    setTextEditorLoading(true);
    setTextEditorOpen(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_BASE_URL}/locations/${selectedLocationId}/documents/${doc.document_id}/text`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTextEditorContent(res.data?.content || '');
    } catch {
      onNotify?.('Could not load file content', 'error');
      setTextEditorOpen(false);
    } finally {
      setTextEditorLoading(false);
    }
  };

  const saveTextEditor = async () => {
    if (!selectedLocationId || !textEditorDoc) return;
    setTextEditorSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE_URL}/locations/${selectedLocationId}/documents/${textEditorDoc.document_id}/text`,
        { content: textEditorContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onNotify?.('Saved', 'success');
      setTextEditorOpen(false);
      refresh();
    } catch {
      onNotify?.('Save failed', 'error');
    } finally {
      setTextEditorSaving(false);
    }
  };

  // ── New text doc ──────────────────────────────────────────────────────────

  const createTextDoc = async () => {
    if (!selectedLocationId || !newTextForm.title.trim()) return;
    setNewTextSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_BASE_URL}/locations/${selectedLocationId}/documents/text`,
        {
          ...newTextForm,
          folder_id: selectedFolderId && selectedFolderId !== UNFILED_FOLDER_ID ? selectedFolderId : '',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onNotify?.('Document created', 'success');
      setNewTextOpen(false);
      setNewTextForm({ title: '', document_type: 'other', content: '' });
      refresh();
    } catch {
      onNotify?.('Could not create document', 'error');
    } finally {
      setNewTextSaving(false);
    }
  };

  // ── Folders ───────────────────────────────────────────────────────────────

  const handleCreateFolder = async () => {
    if (!selectedLocationId || !newFolderName.trim()) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/locations/${selectedLocationId}/documents/folders`,
        { folder_name: newFolderName.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success && res.data.folder) {
        setFolders((prev) => [...prev, res.data.folder]);
        setSelectedFolderId(res.data.folder.folder_id);
        setNewFolderName('');
        setAddFolderOpen(false);
        onNotify?.('Folder created', 'success');
      }
    } catch {
      onNotify?.('Failed to create folder', 'error');
    }
  };

  // ── Rename ────────────────────────────────────────────────────────────────

  const openRenameFolder = (folderId: string, folderName: string) => {
    setRenameType('folder');
    setRenameId(folderId);
    setRenameValue(folderName);
    setRenameOpen(true);
  };

  const openRenameFile = (doc: LocationDocument) => {
    setRenameType('file');
    setRenameId(doc.document_id);
    setRenameValue(doc.title || doc.file_name);
    setRenameOpen(true);
  };

  const saveRename = async () => {
    const name = renameValue.trim();
    if (!selectedLocationId || !name) return;
    setRenameSaving(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      if (renameType === 'folder') {
        await axios.put(
          `${API_BASE_URL}/locations/${selectedLocationId}/documents/folders/${renameId}`,
          { folder_name: name },
          { headers }
        );
        setFolders((prev) => prev.map((f) => (f.folder_id === renameId ? { ...f, folder_name: name } : f)));
      } else {
        await axios.put(
          `${API_BASE_URL}/locations/${selectedLocationId}/documents/${renameId}`,
          { title: name },
          { headers }
        );
        setDocuments((prev) => prev.map((d) => (d.document_id === renameId ? { ...d, title: name } : d)));
      }
      onNotify?.('Renamed', 'success');
      setRenameOpen(false);
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err) && err.response?.data?.message
        ? String(err.response.data.message)
        : 'Rename failed';
      onNotify?.(msg, 'error');
    } finally {
      setRenameSaving(false);
    }
  };

  // ── Archive ───────────────────────────────────────────────────────────────

  const handleArchive = async (doc: LocationDocument) => {
    if (!selectedLocationId) return;
    const confirmed = window.confirm(`Remove "${doc.title}"?\n\nThis will hide the document. It can be recovered by a developer if needed.`);
    if (!confirmed) return;
    setArchiving(doc.document_id);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/locations/${selectedLocationId}/documents/${doc.document_id}/archive`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onNotify?.('Document removed', 'success');
      setDocuments((prev) => prev.filter((d) => d.document_id !== doc.document_id));
      if (selectedFileId === doc.document_id) setSelectedFileId(null);
    } catch {
      onNotify?.('Failed to remove document', 'error');
    } finally {
      setArchiving(null);
    }
  };

  // ── Filtered data ─────────────────────────────────────────────────────────

  const folderEntries = useMemo(
    () => buildVisibleFolderEntries(folders, documents, folderBrowseParentId, UNFILED_FOLDER_ID, 'location'),
    [folders, documents, folderBrowseParentId],
  );

  const handleSelectFolder = (folder: (typeof folderEntries)[number]) => {
    if (folder.folder_id === UNFILED_FOLDER_ID) {
      setSelectedFolderId(UNFILED_FOLDER_ID);
      setSelectedFileId(null);
      return;
    }
    if (folder.hasChildren) {
      setFolderBrowseParentId(folder.folder_id);
      setSelectedFolderId(null);
      setSelectedFileId(null);
      return;
    }
    setSelectedFolderId(folder.folder_id);
    setSelectedFileId(null);
  };

  const folderDocuments = useMemo(() => {
    if (!selectedFolderId) return [];
    const list = selectedFolderId === UNFILED_FOLDER_ID
      ? documents.filter((d) => !d.folder_id)
      : documents.filter((d) => d.folder_id === selectedFolderId);
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((d) => d.title?.toLowerCase().includes(q) || d.file_name?.toLowerCase().includes(q));
  }, [documents, selectedFolderId, searchQuery]);

  const pathLabel = [
    `All ${termLower}s`,
    selectedLocation?.locationName,
    ...buildFolderPathLabels(folderBrowseParentId, selectedFolderId, folders, !!useSiteTerminology),
  ].filter(Boolean).join('  ›  ');

  const activeFile = selectedFileId ? documents.find((d) => d.document_id === selectedFileId) || null : null;
  const activeFileReview = selectedFileId ? docReviews[selectedFileId] : null;
  const hasInspectorContent = aiAnalysisLoading || aiAnalysis || activeFileReview || (selectedFileId && docReviewLoading[selectedFileId]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Box>
      <Paper
        sx={{
          ...pagePaperSx,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        {/* Toolbar */}
        <Box sx={{ px: 1.5, py: 1, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
          <TextField
            size="small"
            placeholder={`Search files in selected ${termLower}…`}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: '1 1 160px', minWidth: 140 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
              endAdornment: searchQuery ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment> : null,
            }}
          />
          <Tooltip title={selectedLocationId ? `Upload a file into the selected ${termLower} / folder` : `Select a ${termLower} first`}>
            <span>
              <Button size="small" variant="outlined" disabled={!selectedLocationId || uploading}
                startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}>
                {uploading ? 'Uploading…' : 'Upload'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={selectedLocationId ? 'Create a new folder' : `Select a ${termLower} first`}>
            <span>
              <Button size="small" variant="outlined" disabled={!selectedLocationId}
                startIcon={<CreateNewFolderIcon />}
                onClick={() => { setNewFolderName(''); setAddFolderOpen(true); }}>
                New folder
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={selectedLocationId ? 'Create a new text document' : `Select a ${termLower} first`}>
            <span>
              <Button size="small" variant="outlined" disabled={!selectedLocationId}
                startIcon={<NoteAddIcon />}
                onClick={() => { setNewTextForm({ title: '', document_type: 'other', content: '' }); setNewTextOpen(true); }}>
                New text
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={selectedLocationId ? `AI compliance overview of this ${termLower}'s documents` : `Select a ${termLower} first`}>
            <span>
              <Button size="small" variant="contained" color="secondary" disabled={!selectedLocationId || aiAnalysisLoading}
                startIcon={aiAnalysisLoading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
                onClick={handleRunAiAnalysis}>
                {aiAnalysisLoading ? 'Analysing…' : 'AI analyse'}
              </Button>
            </span>
          </Tooltip>
          <input ref={fileInputRef} type="file" hidden onChange={handleFileInputChange} />
        </Box>

        {/* Context bar */}
        {selectedLocation && (
          <Box sx={{ px: 1.5, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: 'secondary.50', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: 1 }}>{pathLabel}</Typography>
            {selectedLocation.address && (
              <Typography variant="caption" color="text.secondary" noWrap>{selectedLocation.address}</Typography>
            )}
          </Box>
        )}

        {/* Three columns */}
        <Box sx={{ display: 'flex', flex: 1, minHeight: 360, maxHeight: 'min(50vh, 440px)', overflow: 'hidden' }}>
          {/* Column 1 — Locations */}
          <Box sx={columnSx}>
            <Box sx={columnHeaderSx}>
              <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>
                {termPluralTitle} ({locations.length})
              </Typography>
            </Box>
            <List dense disablePadding sx={{ overflow: 'auto', flex: 1, py: 0 }}>
              {loadingLocations ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
              ) : locations.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>No {termLower}s</Typography>
              ) : locations.map((loc) => {
                const sel = selectedLocationId === loc.locationId;
                return (
                  <ListItemButton key={loc.locationId} onClick={() => selectLocation(loc)} sx={sel ? selectedRowSx : undefined}>
                    <ListItemIcon sx={{ minWidth: 36 }}><LocationOnIcon fontSize="small" color={sel ? 'inherit' : 'primary'} /></ListItemIcon>
                    <ListItemText
                      primary={loc.locationName}
                      secondary={loc.address}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                    <ChevronRightIcon fontSize="small" sx={{ opacity: 0.4, ml: 0.5 }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>

          {/* Column 2 — Folders */}
          <Box sx={{ ...columnSx, opacity: selectedLocationId ? 1 : 0.4 }}>
            <Box sx={{ ...columnHeaderSx, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {folderBrowseParentId && (
                <Tooltip title="Back to folders">
                  <IconButton
                    size="small"
                    onClick={() => {
                      const parent = folders.find((f) => f.folder_id === folderBrowseParentId);
                      setFolderBrowseParentId(parent?.parent_folder_id || null);
                      setSelectedFolderId(null);
                      setSelectedFileId(null);
                    }}
                    sx={{ p: 0.25, mr: 0.25 }}
                  >
                    <ArrowBackIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
              <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5} noWrap sx={{ flex: 1 }}>
                {folderBrowseParentId
                  ? folders.find((f) => f.folder_id === folderBrowseParentId)?.folder_name || 'Folders'
                  : 'Folders'}
              </Typography>
              {selectedLocationId && (
                <Tooltip title="Create folder">
                  <IconButton size="small" onClick={() => { setNewFolderName(''); setAddFolderOpen(true); }} sx={{ p: 0.25 }}>
                    <CreateNewFolderIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <List dense disablePadding sx={{ overflow: 'auto', flex: 1, py: 0 }}>
              {!selectedLocationId ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>Select a {termLower}</Typography>
              ) : loadingDocs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
              ) : folderEntries.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>No folders yet</Typography>
                  <Button size="small" variant="outlined" startIcon={<CreateNewFolderIcon />} onClick={() => { setNewFolderName(''); setAddFolderOpen(true); }}>
                    Create folder
                  </Button>
                </Box>
              ) : folderEntries.map((folder) => {
                const sel = selectedFolderId === folder.folder_id;
                const isVirtual = folder.folder_id === UNFILED_FOLDER_ID;
                const isSystemFolder = !isVirtual && isSystemCheckReportFolder(folder, folders);
                const countLabel = folder.hasChildren
                  ? `${folder.count} file${folder.count === 1 ? '' : 's'} total`
                  : `${folder.count} file${folder.count === 1 ? '' : 's'}`;
                return (
                  <Box
                    key={folder.folder_id}
                    sx={{
                      display: 'flex',
                      alignItems: 'stretch',
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: sel ? 'primary.main' : undefined,
                      color: sel ? 'primary.contrastText' : undefined,
                      '& .MuiListItemIcon-root': sel ? { color: 'primary.contrastText' } : undefined,
                    }}
                  >
                    <ListItemButton
                      sx={{ flex: 1, py: 0.75, ...(sel ? { color: 'inherit' } : {}) }}
                      onClick={() => handleSelectFolder(folder)}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <FolderIcon fontSize="small" sx={{ color: sel ? 'inherit' : isVirtual ? 'action.active' : 'warning.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={displayCheckReportSubfolderName(folder.folder_name, !!useSiteTerminology)}
                        secondary={countLabel}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600, noWrap: true, color: 'inherit' }}
                        secondaryTypographyProps={{ variant: 'caption', color: sel ? 'rgba(255,255,255,0.7)' : undefined }}
                      />
                      <ChevronRightIcon fontSize="small" sx={{ opacity: 0.4, ml: 0.5 }} />
                    </ListItemButton>
                    {!isVirtual && !isSystemFolder && (
                      <Box sx={{ display: 'flex', alignItems: 'center', pr: 0.5 }}>
                        <Tooltip title="Rename folder">
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); openRenameFolder(folder.folder_id, folder.folder_name); }}
                            sx={{ color: sel ? 'primary.contrastText' : 'primary.main', opacity: 0.85 }}
                          >
                            <EditIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </List>
          </Box>

          {/* Column 3 — Files */}
          <Box
            sx={{ ...columnSx, flex: '1.6 1 0', maxWidth: 'none', opacity: selectedFolderId ? 1 : 0.4, position: 'relative' }}
            onDragOver={(e) => { if (!selectedLocationId) return; e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <Box sx={{ ...columnHeaderSx, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5} sx={{ flex: 1 }} noWrap>
                {selectedFolderId
                  ? displayCheckReportSubfolderName(
                      folderEntries.find((f) => f.folder_id === selectedFolderId)?.folder_name || 'Files',
                      !!useSiteTerminology,
                    )
                  : 'Files'}
              </Typography>
              {selectedLocationId && (
                <Tooltip title="Drop files here or click Upload in the toolbar">
                  <UploadFileIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'pointer' }} onClick={() => fileInputRef.current?.click()} />
                </Tooltip>
              )}
            </Box>

            {/* Drag-over overlay */}
            {dragOver && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 10,
                bgcolor: 'rgba(25, 118, 210, 0.08)',
                border: '2px dashed',
                borderColor: 'primary.main',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
                borderRadius: 0,
              }}>
                <Box sx={{ textAlign: 'center' }}>
                  <UploadFileIcon sx={{ fontSize: 36, color: 'primary.main', mb: 0.5 }} />
                  <Typography variant="body2" color="primary" fontWeight={600}>Drop to upload</Typography>
                  {selectedFolderId && selectedFolderId !== UNFILED_FOLDER_ID && (
                    <Typography variant="caption" color="text.secondary">
                      Into: {displayCheckReportSubfolderName(
                        folderEntries.find((f) => f.folder_id === selectedFolderId)?.folder_name || '',
                        !!useSiteTerminology,
                      )}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            <List dense disablePadding sx={{ overflow: 'auto', flex: 1, py: 0 }}>
              {!selectedFolderId ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Select a folder</Typography>
                  {selectedLocationId && (
                    <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                      or drag &amp; drop files anywhere here
                    </Typography>
                  )}
                </Box>
              ) : folderDocuments.length === 0 ? (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Empty folder</Typography>
                  <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>Drag &amp; drop or use Upload</Typography>
                </Box>
              ) : folderDocuments.map((doc) => {
                const isText = doc.file_type === 'text' || doc.mime_type === 'text/plain';
                const fileSel = selectedFileId === doc.document_id;
                const reviewing = !!docReviewLoading[doc.document_id];
                const hasReview = !!docReviews[doc.document_id];
                return (
                  <Box key={doc.document_id} sx={{ display: 'flex', alignItems: 'stretch', borderBottom: 1, borderColor: 'divider', bgcolor: fileSel ? 'action.selected' : undefined }}>
                    <ListItemButton sx={{ flex: 1, py: 0.75 }} onClick={() => {
                      setSelectedFileId(doc.document_id);
                      if (isText) openTextEditor(doc);
                      else window.open(doc.file_url, '_blank', 'noopener,noreferrer');
                    }}>
                      <ListItemIcon sx={{ minWidth: 36 }}><FileTypeIcon fileType={doc.file_type} /></ListItemIcon>
                      <ListItemText
                        primary={doc.title}
                        secondary={
                          <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                            <span>{[formatSize(doc.file_size), docTypeLabel(doc.document_type)].filter(Boolean).join(' · ')}</span>
                            {doc.created_at && <span>{formatDateTime(doc.created_at)}</span>}
                          </Box>
                        }
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 500, noWrap: true }}
                        secondaryTypographyProps={{ variant: 'caption', component: 'span' } as any}
                      />
                    </ListItemButton>
                    <Box sx={{ display: 'flex', alignItems: 'center', pr: 0.5, gap: 0 }}>
                      {hasReview && (
                        <Chip
                          label={docReviews[doc.document_id].verdict?.replace(/_/g, ' ')}
                          size="small"
                          color={verdictColor(docReviews[doc.document_id].verdict)}
                          sx={{ height: 18, fontSize: '0.55rem', mr: 0.25 }}
                        />
                      )}
                      <Tooltip title="AI compliance review">
                        <span>
                          <IconButton size="small" disabled={reviewing} onClick={() => handleRunDocumentReview(doc)} sx={{ color: 'secondary.main' }}>
                            {reviewing ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Rename file">
                        <IconButton size="small" onClick={() => openRenameFile(doc)} sx={{ color: 'primary.main' }}>
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      {isText && (
                        <Tooltip title="Edit text content">
                          <IconButton size="small" onClick={() => openTextEditor(doc)} sx={{ color: 'primary.main' }}>
                            <EditNoteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Open in new tab">
                        <IconButton size="small" onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove document">
                        <span>
                          <IconButton
                            size="small"
                            disabled={archiving === doc.document_id}
                            onClick={() => handleArchive(doc)}
                            sx={{ color: 'error.main', opacity: 0.7, '&:hover': { opacity: 1 } }}
                          >
                            {archiving === doc.document_id
                              ? <CircularProgress size={16} color="inherit" />
                              : <DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })}
            </List>
          </Box>

          {/* Column 4 — AI Inspector (right panel) */}
          <Box sx={{
            flex: '0 0 280px',
            minWidth: 240,
            maxWidth: 320,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: 0,
            bgcolor: 'grey.50',
          }}>
            <Box sx={{ ...columnHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AutoAwesomeIcon fontSize="small" color="secondary" />
                <Typography variant="caption" fontWeight={700} textTransform="uppercase" letterSpacing={0.5}>AI Inspector</Typography>
              </Box>
              {hasInspectorContent && (
                <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }} onClick={() => { setAiAnalysis(null); setInspectorMode('idle'); setSelectedFileId(null); setDocReviews({}); }}>Clear</Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', px: 1.5, pb: 0.75, display: 'block', bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
              Powered by Policy Max
            </Typography>
            <Box sx={{ overflow: 'auto', flex: 1, p: 1.5 }}>
              {!hasInspectorContent && (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', opacity: 0.35, gap: 1, py: 4 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 32, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.disabled" textAlign="center">
                    Select a file and click {'\u2728'} to review, or click AI analyse for a {termLower} overview
                  </Typography>
                </Box>
              )}
              {(aiAnalysisLoading || (inspectorMode === 'file' && !activeFileReview && docReviewLoading[selectedFileId || ''])) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">{aiAnalysisLoading ? 'Analysing…' : 'Reviewing…'}</Typography>
                </Box>
              )}
              {inspectorMode === 'location' && aiAnalysis && !aiAnalysisLoading && (
                <AiLocationAnalysisPanel analysis={aiAnalysis} onDismiss={() => { setAiAnalysis(null); setInspectorMode('idle'); }} />
              )}
              {inspectorMode === 'file' && activeFileReview && activeFile && (
                <AiDocumentReviewPanel review={activeFileReview} title={activeFile.title} onDismiss={() => { setInspectorMode(aiAnalysis ? 'location' : 'idle'); setSelectedFileId(null); }} />
              )}
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Text editor dialog */}
      <Dialog open={textEditorOpen} onClose={() => setTextEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsertDriveFileIcon color="success" />
          {textEditorDoc?.title || 'Text file'}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontWeight: 400 }}>— edit & save</Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {textEditorLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <TextField
              multiline fullWidth minRows={18} maxRows={32}
              value={textEditorContent}
              onChange={(e) => setTextEditorContent(e.target.value)}
              placeholder="Start typing…"
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 } }}
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {textEditorContent.length.toLocaleString()} chars · {textEditorContent.split('\n').length} lines
          </Typography>
          <Button onClick={() => setTextEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={textEditorLoading || textEditorSaving}
            startIcon={textEditorSaving ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={saveTextEditor}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create folder dialog */}
      <Dialog open={addFolderOpen} onClose={() => { setAddFolderOpen(false); setNewFolderName(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Create folder{selectedLocation ? ` — ${selectedLocation.locationName}` : ''}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Folder name"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && newFolderName.trim()) handleCreateFolder(); }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => { setAddFolderOpen(false); setNewFolderName(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFolder} disabled={!newFolderName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Rename folder / file dialog */}
      <Dialog open={renameOpen} onClose={() => !renameSaving && setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{renameType === 'folder' ? 'Rename folder' : 'Rename file'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={renameType === 'folder' ? 'Folder name' : 'Display name'}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && renameValue.trim()) saveRename(); }}
            sx={{ mt: 1 }}
            helperText={renameType === 'file' ? 'Updates the title shown in the file list.' : undefined}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRenameOpen(false)} disabled={renameSaving}>Cancel</Button>
          <Button variant="contained" disabled={!renameValue.trim() || renameSaving} onClick={saveRename}>
            {renameSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New text document dialog */}
      <Dialog open={newTextOpen} onClose={() => setNewTextOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>New text document{selectedLocation ? ` — ${selectedLocation.locationName}` : ''}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Title *" size="small" sx={{ flex: 2 }}
                value={newTextForm.title}
                onChange={(e) => setNewTextForm((p) => ({ ...p, title: e.target.value }))} />
              <TextField select label="Document type" size="small" sx={{ flex: 1 }}
                value={newTextForm.document_type}
                onChange={(e) => setNewTextForm((p) => ({ ...p, document_type: e.target.value }))}>
                {DOC_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Box>
            <TextField
              label="Content"
              multiline minRows={16}
              fullWidth
              placeholder="Type or paste content here…"
              value={newTextForm.content}
              onChange={(e) => setNewTextForm((p) => ({ ...p, content: e.target.value }))}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 13, lineHeight: 1.6 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {newTextForm.content.length.toLocaleString()} chars
            {selectedFolderId && selectedFolderId !== UNFILED_FOLDER_ID
              ? ` · will be saved to "${displayCheckReportSubfolderName(
                  folderEntries.find((f) => f.folder_id === selectedFolderId)?.folder_name || '',
                  !!useSiteTerminology,
                )}"`
              : ''}
          </Typography>
          <Button onClick={() => setNewTextOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!newTextForm.title.trim() || newTextSaving}
            startIcon={newTextSaving ? <CircularProgress size={14} color="inherit" /> : null}
            onClick={createTextDoc}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationFilesExplorer;
